
import { useState } from 'react';

import { useDispatch } from 'react-redux';

import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

import { openSnackbar } from '../store/slices/SnackbarSlice';

import { FormatPhoneNumber } from '../helpers/FormatPhoneNumber';

import { BuscarUsuarioPoderEnLinea } from '../connections/admin/UsuarioConnection';
import { AutorizarExpedienteVirtual } from '../connections/tribunal/ExpedienteVirtualConnection';

import { defaultUsuarioPoderEnLinea, UsuarioPoderEnLineaProps } from '../interfaces/admin/UsuarioInterface';
import { ExpedienteAutorizadoProps } from '../interfaces/tribunal/ExpedienteVirtualInterface';

interface Props {
    openDialog:         boolean;
    idExpediente:       number;
    handleCloseDialog:  () => void;
    onAutorizado:       ( expedienteAutorizado: ExpedienteAutorizadoProps ) => void;
}

export const DialogAutorizarExpedienteVirtual = ( { openDialog, idExpediente, handleCloseDialog, onAutorizado }: Props ) => {

    const dispatch = useDispatch();

    const [cuentaPL, setCuentaPL] = useState( '' );
    const [usuarioPL, setUsuarioPL] = useState<UsuarioPoderEnLineaProps>( defaultUsuarioPoderEnLinea );

    const [loadingCuentaPL, setLoadingCuentaPL] = useState( false );
    const [loadingAutorizar, setLoadingAutorizar] = useState( false );

    const [encontrado, setEncontrado] = useState( false );
    const [mensajeNoEncontrado, setMensajeNoEncontrado] = useState( '' );

    const handleBuscarUsuarioPoderEnLinea = async () => {

        const isEmail = cuentaPL.includes('@');

        setLoadingCuentaPL( true );

        await BuscarUsuarioPoderEnLinea({
            correoElectronico: isEmail ? cuentaPL : '',
            telefonoCelular: !isEmail ? FormatPhoneNumber( cuentaPL ) : '',
        }).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                setCuentaPL( '' );
                setUsuarioPL( success ? resp : defaultUsuarioPoderEnLinea );
                setEncontrado( success );
                setMensajeNoEncontrado( success ? '' : message );
                setLoadingCuentaPL( false );

            }, 700);

        });

    }

    const handleAutorizar = async () => {

        setLoadingAutorizar( true );

        await AutorizarExpedienteVirtual({ idExpediente, usuarioPoderEnLinea: usuarioPL }).then( resp => {

            setTimeout(() => {

                const { success, message, expedienteAutorizado } = resp;

                if( success ){

                    onAutorizado( expedienteAutorizado );

                    dispatch( openSnackbar({ message, variant: 'success' }) );

                    setLoadingAutorizar( false );

                    handleCerrarDialog();

                }
                else {

                    dispatch( openSnackbar({ message, variant: 'error' }) );

                    setLoadingAutorizar( false );

                }

            }, 700);

        });

    }

    const handleCerrarDialog = () => {

        setCuentaPL( '' );
        setUsuarioPL( defaultUsuarioPoderEnLinea );
        setEncontrado( false );
        setMensajeNoEncontrado( '' );

        handleCloseDialog();

    }

    return (

        <Dialog
            fullWidth
            maxWidth='sm'
            open={ openDialog }
            onClose={ () => {} }
        >

            <DialogTitle>

                <Typography variant='h6' fontWeight={600}>
                    Autorizar expediente virtual
                </Typography>

                <Typography variant='body2' color='text.secondary'>
                    Busca al solicitante para darle acceso
                </Typography>

            </DialogTitle>

            <Divider />

            <DialogContent>

                <Stack spacing={2.5} mt={1}>

                    <TextField
                        label='Correo electrónico o teléfono celular'
                        variant='outlined'
                        size='medium'
                        fullWidth
                        autoFocus
                        autoComplete='off'
                        value={ cuentaPL }
                        onChange={ (e) => { setCuentaPL( e.target.value ); setEncontrado( false ); setMensajeNoEncontrado( '' ); } }
                        onKeyDown={ (e) => { if( e.key === 'Enter' && cuentaPL !== '' ) handleBuscarUsuarioPoderEnLinea(); } }
                        slotProps={{
                            input: {                                
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton
                                            disabled={ cuentaPL === '' || loadingCuentaPL }
                                            onClick={ handleBuscarUsuarioPoderEnLinea }
                                            sx={{
                                                bgcolor: 'primary.main',
                                                color: 'primary.contrastText',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                                            }}
                                        >
                                            { loadingCuentaPL ? <CircularProgress size={20} color='inherit' /> : <SearchIcon /> }
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                            inputLabel: { shrink: true },
                        }}
                    />

                    {
                        !encontrado
                        ?
                            mensajeNoEncontrado
                            ?
                                <Alert severity='warning' variant='filled'>
                                    { mensajeNoEncontrado }
                                </Alert>
                            :
                                <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>

                                    <PersonSearchIcon sx={{ fontSize: 44, opacity: 0.3 }} />

                                    <Typography variant='body2' mt={1}>
                                        Aún no has buscado a ningún solicitante
                                    </Typography>

                                </Box>
                        :
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >

                                <Box>

                                    <Typography variant='subtitle2' fontWeight={600}>
                                        { usuarioPL.solicitante }
                                    </Typography>

                                    <Stack direction='row' spacing={3} mt={0.5}>

                                        <Stack direction='row' spacing={0.5} alignItems='center'>
                                            <Typography variant='caption' color='text.secondary'>
                                                { usuarioPL.correoElectronico }
                                            </Typography>
                                        </Stack>

                                        <Stack direction='row' spacing={0.5} alignItems='center'>
                                            <Typography variant='caption' color='text.secondary'>
                                                { usuarioPL.telefonoCelular }
                                            </Typography>
                                        </Stack>

                                    </Stack>

                                </Box>

                            </Box>
                    }

                </Stack>

            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2 }}>

                <Button color='secondary' disabled={ loadingAutorizar } onClick={ handleCerrarDialog }>
                    Cancelar
                </Button>

                <Button
                    variant='contained'
                    disabled={ !encontrado }
                    loading={ loadingAutorizar }
                    onClick={ handleAutorizar }
                >
                    Autorizar
                </Button>

            </DialogActions>

        </Dialog>

    );

}
